import React from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { TacticCard } from './TacticCard'
import type { MECETactic, MECEPillar, ImplementationStatus } from '../../../models'
import './SortableTacticList.css'

interface SortableTacticListProps {
  tactics: MECETactic[]
  pillarName: string
  pillarIndex: number
  pillar?: MECEPillar         // Full pillar object for numbering utility
  isReorderMode: boolean
  onReorder: (pillarIndex: number, newOrder: MECETactic[]) => void
  onQuickAsk?: (tactic: MECETactic, pillarName: string, tacticIndex: number) => void
  onViewEvidence?: (tactic: MECETactic) => void
  onEditTactic?: (pillarIndex: number, tacticIndex: number) => void
  onStatusChange?: (pillarIndex: number, tacticIndex: number, status: ImplementationStatus) => void
}

/**
 * Generate unique ID for a tactic within a pillar
 */
function getTacticId(pillarIndex: number, tacticIndex: number, tactic: MECETactic): string {
  // Use initiative_id if available, otherwise construct from indices
  if ((tactic as any).initiative_id) {
    return (tactic as any).initiative_id
  }
  return `pillar_${pillarIndex}_tactic_${tacticIndex}_${tactic.title?.slice(0, 20) || 'unnamed'}`
}

export const SortableTacticList: React.FC<SortableTacticListProps> = ({
  tactics,
  pillarName,
  pillarIndex,
  pillar,
  isReorderMode,
  onReorder,
  onQuickAsk,
  onViewEvidence,
  onEditTactic,
  onStatusChange
}) => {
  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,  // Start drag after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Generate stable IDs for all tactics
  const tacticIds = tactics.map((tactic, index) => getTacticId(pillarIndex, index, tactic))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = tacticIds.indexOf(active.id as string)
      const newIndex = tacticIds.indexOf(over.id as string)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(tactics, oldIndex, newIndex)
        onReorder(pillarIndex, newOrder)
      }
    }
  }

  if (tactics.length === 0) {
    return (
      <div className="sortable-tactic-list empty">
        <p className="no-tactics-message">No tactics defined for this pillar.</p>
      </div>
    )
  }

  return (
    <div className="sortable-tactic-list">
      {isReorderMode && (
        <div className="reorder-instructions">
          Drag tactics to reorder priorities. Changes are saved automatically.
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tacticIds}
          strategy={verticalListSortingStrategy}
        >
          {tactics.map((tactic, index) => {
            const tacticId = getTacticId(pillarIndex, index, tactic)

            return (
              <TacticCard
                key={tacticId}
                tactic={tactic}
                tacticId={tacticId}
                pillarName={pillarName}
                pillar={pillar}
                pillarIndex={pillarIndex}
                tacticIndex={index}
                isReorderMode={isReorderMode}
                userPriority={isReorderMode ? index + 1 : undefined}
                implementationStatus={(tactic as any).implementation_status || 'not_started'}
                onStatusChange={onStatusChange ? (status) => onStatusChange(pillarIndex, index, status) : undefined}
                onQuickAsk={onQuickAsk ? () => onQuickAsk(tactic, pillarName, index) : undefined}
                onViewEvidence={onViewEvidence ? () => onViewEvidence(tactic) : undefined}
                onEdit={onEditTactic ? () => onEditTactic(pillarIndex, index) : undefined}
              />
            )
          })}
        </SortableContext>
      </DndContext>
    </div>
  )
}
